import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLazyGetBenefitLineAnalysisQuery } from "store/queries/benefitComparison";
import {
  ComparisonBenefit,
  IComparisonExample,
} from "types/BenefitComparison/BenefitComparison";
import {
  ILineAnalysis,
  ILineAnalysisRow,
  LineAnalysisLocation,
  LineAnalysisStatus,
} from "types/BenefitComparison/LineAnalysis";

import { formatComparisonValue } from "./benefitComparisonWording";

/**
 * "Analyze line items" — on the Engine worklist's response panel.
 *
 * The button re-runs today's engine on the selected check with recording on; every row of the panel
 * then carries a marker (Picked / Dropped / Not used / Not read) whose tooltip is the engine's own
 * reason, filled into a fixed sentence on the server. 🚨 No AI is involved anywhere in this.
 *
 * Kept in its own file, with the panel touching it at four points only (the provider around the
 * panel body, the toolbar button, the output section above the table, and one marker per row), so
 * the panel file stays easy to merge.
 */

export const ANALYZE_BUTTON_LABEL = "Analyze line items";
export const ANALYZE_AGAIN_LABEL = "Analyze again";

export const LINE_ANALYSIS_STATUS_LABELS: Record<LineAnalysisStatus, string> = {
  PICKED: "Picked",
  DROPPED: "Dropped",
  NOT_USED: "Not used",
  NOT_READ: "Not read",
};

interface ITagTone {
  severity?: "success" | "danger" | "warning";
  className?: string;
}

const GREY: ITagTone = { className: "bg-gray-200 text-900" };

/**
 * Colour means something: green Picked, red Dropped, and amber Not used ONLY for a real candidate —
 * a line of this benefit's kind in a care setting the engine read, which lost. A line that was never
 * in the running (another care setting, another kind of line) is neutral grey, whatever its status.
 */
const toneOf = (row: ILineAnalysisRow): ITagTone => {
  if (row.status === "PICKED") return { severity: "success" };
  if (row.status === "DROPPED") return { severity: "danger" };
  if (row.status === "NOT_USED" && row.ofBenefit && row.careSetting === "READ")
    return { severity: "warning" };
  return GREY;
};

/** Matched by one `<Tooltip>` per analysis — PrimeReact needs a mounted Tooltip targeting the class. */
const MARKER_CLASS = "lineAnalysisMarker";

const keyOf = (location: LineAnalysisLocation) =>
  location.type === "LINE"
    ? `L:${location.serviceIndex}:${location.lineIndex}`
    : `S:${location.block}:${location.field}`;

interface ILineAnalysisContext {
  analysis: ILineAnalysis | null;
  rows: Map<string, ILineAnalysisRow>;
  shown: Set<string>;
  benefit: ComparisonBenefit;
  run: () => void;
  isFetching: boolean;
  isError: boolean;
}

const LineAnalysisContext = createContext<ILineAnalysisContext | null>(null);

/**
 * Holds one check's analysis for the panel inside it. Selecting another check drops it: an analysis
 * is of one check and must never be shown against another's lines.
 */
export function LineAnalysisProvider({
  example,
  benefit,
  children,
}: {
  example: IComparisonExample;
  benefit: ComparisonBenefit;
  children: ReactNode;
}) {
  const [trigger, { data, isFetching, isError }] =
    useLazyGetBenefitLineAnalysisQuery();
  const [requested, setRequested] = useState(false);
  useEffect(() => setRequested(false), [example.pverifyResponseId, benefit]);

  const current = data?.data;
  const analysis =
    requested &&
    current &&
    current.pverifyResponseId === example.pverifyResponseId &&
    current.benefit === benefit
      ? current
      : null;

  const value = useMemo<ILineAnalysisContext>(() => {
    const rows = new Map<string, ILineAnalysisRow>(
      (analysis?.lines ?? []).map((row) => [keyOf(row.location), row])
    );
    const shown = new Set<string>([
      ...example.summaries.map((summary) =>
        keyOf({ type: "SUMMARY", block: summary.block, field: summary.field })
      ),
      ...example.lines.map((line) =>
        keyOf({
          type: "LINE",
          serviceIndex: line.serviceIndex,
          lineIndex: line.lineIndex,
        })
      ),
    ]);
    return {
      analysis,
      rows,
      shown,
      benefit,
      run: () => {
        setRequested(true);
        trigger({ pverifyResponseId: example.pverifyResponseId, benefit });
      },
      isFetching,
      isError: requested && isError,
    };
  }, [analysis, example, benefit, trigger, isFetching, isError, requested]);

  return (
    <LineAnalysisContext.Provider value={value}>
      {analysis && (
        <Tooltip
          key={`${analysis.pverifyResponseId}:${analysis.benefit}`}
          target={`.${MARKER_CLASS}`}
          position="left"
        />
      )}
      {children}
    </LineAnalysisContext.Provider>
  );
}

const useLineAnalysis = () => useContext(LineAnalysisContext);

function StatusTag({ row }: { row: ILineAnalysisRow }) {
  const label = LINE_ANALYSIS_STATUS_LABELS[row.status];
  const tone = toneOf(row);
  return (
    <Tag
      value={label}
      severity={tone.severity}
      className={`${MARKER_CLASS} white-space-nowrap ${tone.className ?? ""}`}
      data-pr-tooltip={row.reason}
      aria-label={`${label}: ${row.reason}`}
      data-testid={`lineAnalysis.marker.${row.path}`}
    />
  );
}

/** One row's marker, beside its tags. Nothing until the analysis has run for this check. */
export function LineAnalysisMarker({
  location,
}: {
  location: LineAnalysisLocation;
}) {
  const context = useLineAnalysis();
  const row = context?.rows.get(keyOf(location));
  return row ? <StatusTag row={row} /> : null;
}

/** The server's sentence without its "Not used: " lead — the tag beside it already says that. */
const reasonText = (row: ILineAnalysisRow) => {
  const text = row.reason.replace(
    /^(Picked|Dropped|Not used|Not read):\s*/,
    ""
  );
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/** status | care setting | message | value — the tag column fixed, the message clamped to one line. */
const ROW_GRID = {
  display: "grid",
  gridTemplateColumns: "5.5rem minmax(6rem, 14rem) minmax(0, 1fr) auto",
  columnGap: "0.75rem",
  alignItems: "center",
};

/** Inside a care-setting sub-group the heading names it, so the message and value take its space. */
const ROW_GRID_NO_SETTING = {
  ...ROW_GRID,
  gridTemplateColumns: "5.5rem minmax(0, 1fr) auto",
};

/** Lines up a row's own sentence under the column after the tag. */
const REASON_INDENT = { marginLeft: "6.25rem" };

const careSettingOf = (row: ILineAnalysisRow) =>
  row.serviceName ??
  (row.location.type === "SUMMARY" ? row.location.block : "—");

const lineTypeOf = (row: ILineAnalysisRow) => row.lineType ?? "Untyped";

/** `[[key, rows], …]`, the largest first, then by name. */
const groupBy = (
  rows: ILineAnalysisRow[],
  keyFn: (row: ILineAnalysisRow) => string
): [string, ILineAnalysisRow[]][] => {
  const groups = new Map<string, ILineAnalysisRow[]>();
  rows.forEach((row) => {
    const key = keyFn(row);
    groups.set(key, [...(groups.get(key) ?? []), row]);
  });
  return Array.from(groups.entries()).sort(
    ([a, aRows], [b, bRows]) =>
      bRows.length - aRows.length || a.localeCompare(b)
  );
};

/**
 * Compact rows. `coveredBy` is the reason code the group's header already states: a row with that
 * code shows no sentence of its own (it stays in the tag's tooltip); any other row shows its own.
 * The raw path is only in the row's title — it is the least readable part.
 */
function RowList({
  rows,
  coveredBy,
  showCareSetting = true,
}: {
  rows: ILineAnalysisRow[];
  coveredBy?: string;
  showCareSetting?: boolean;
}) {
  const context = useLineAnalysis();
  const grid = showCareSetting ? ROW_GRID : ROW_GRID_NO_SETTING;
  return (
    <div role="list" className="flex flex-column gap-1 text-sm">
      {rows.map((row) => {
        const message = row.message.join(" | ");
        return (
          <div
            key={row.path}
            role="listitem"
            title={row.path}
            data-testid={`lineAnalysis.row.${row.path}`}
          >
            <div style={grid}>
              <span>
                <StatusTag row={row} />
              </span>
              {showCareSetting && (
                <span
                  className="white-space-nowrap overflow-hidden text-overflow-ellipsis"
                  data-testid="lineAnalysis.row.careSetting"
                >
                  {careSettingOf(row)}
                </span>
              )}
              <span
                className="white-space-nowrap overflow-hidden text-overflow-ellipsis text-color-secondary"
                title={message}
                data-testid="lineAnalysis.row.message"
              >
                {message || "—"}
              </span>
              <span
                className="text-right white-space-nowrap"
                data-testid="lineAnalysis.row.value"
              >
                {row.value !== null && context
                  ? formatComparisonValue(row.value, context.benefit)
                  : ""}
              </span>
            </div>
            {row.reasonCode !== coveredBy && (
              <div
                className="text-xs text-color-secondary mt-1"
                style={REASON_INDENT}
              >
                {reasonText(row)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Toggle({
  label,
  open,
  onClick,
  testId,
}: {
  label: string;
  open: boolean;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <Button
      type="button"
      icon={open ? "pi pi-angle-down" : "pi pi-angle-right"}
      label={label}
      className="p-button-text p-button-sm px-0"
      onClick={onClick}
      data-testid={testId}
    />
  );
}

function Group({
  label,
  count,
  testId,
  defaultOpen = false,
  children,
}: {
  label: string;
  count: number;
  testId: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (count === 0) return null;
  return (
    <div data-testid={testId}>
      <Toggle label={label} open={open} onClick={() => setOpen(!open)} />
      {open && <div className="pl-4 flex flex-column gap-2">{children}</div>}
    </div>
  );
}

function GroupReason({ text }: { text: string | null }) {
  return text ? (
    <p className="m-0 text-sm text-color-secondary">{text}</p>
  ) : null;
}

/** Lines in other care settings: the reason once, then a short block per care setting. */
function OtherSettings({
  rows,
  reason,
}: {
  rows: ILineAnalysisRow[];
  reason: string | null;
}) {
  return (
    <>
      <GroupReason text={reason} />
      {groupBy(rows, careSettingOf).map(([setting, settingRows]) => (
        <div key={setting} className="flex flex-column gap-1">
          <span className="text-sm font-semibold">{`${setting} · ${settingRows.length}`}</span>
          <RowList
            rows={settingRows}
            coveredBy={reason ? "OTHER_CARE_SETTING" : undefined}
            showCareSetting={false}
          />
        </div>
      ))}
    </>
  );
}

function KindGroup({ kind, rows }: { kind: string; rows: ILineAnalysisRow[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <Toggle
        label={`${kind} · ${rows.length}`}
        open={open}
        onClick={() => setOpen(!open)}
        testId={`lineAnalysis.kind.${kind}`}
      />
      {open && (
        <div className="pl-4">
          <RowList rows={rows} coveredBy="WRONG_LINE_TYPE" />
        </div>
      )}
    </div>
  );
}

/** Lines of other kinds: the reason once, then a count per line type, each opening to its rows. */
function OtherKinds({
  rows,
  reason,
}: {
  rows: ILineAnalysisRow[];
  reason: string;
}) {
  return (
    <>
      <GroupReason text={reason} />
      <div className="flex flex-column">
        {groupBy(rows, lineTypeOf).map(([kind, kindRows]) => (
          <KindGroup key={kind} kind={kind} rows={kindRows} />
        ))}
      </div>
    </>
  );
}

/** The toolbar button: runs the analysis, and re-runs it once there is one. */
export function AnalyzeLineItemsButton() {
  const context = useLineAnalysis();
  if (!context) return null;
  const { analysis, run, isFetching } = context;
  return (
    <Button
      type="button"
      icon="pi pi-list"
      label={analysis ? ANALYZE_AGAIN_LABEL : ANALYZE_BUTTON_LABEL}
      className="p-button-outlined p-button-sm"
      loading={isFetching}
      onClick={run}
      data-testid="lineAnalysis.run"
    />
  );
}

/**
 * The output, under its own heading: the one-line outcome, and the lines the panel does not show —
 * those in the care setting(s) the engine read first and open (they are the candidates), then the
 * noise (other care settings, other kinds of line) collapsed to a count, each stating its reason once.
 * Nothing until the analysis has run for this check.
 */
export function LineAnalysisSection({
  className = "",
}: {
  className?: string;
}) {
  const context = useLineAnalysis();
  if (!context) return null;
  const { analysis, shown, isError } = context;
  if (!analysis && !isError) return null;

  const hidden = (analysis?.lines ?? []).filter(
    (row) => !shown.has(keyOf(row.location))
  );
  const readHere = hidden.filter(
    (row) => row.ofBenefit && row.careSetting === "READ"
  );
  const otherSettings = hidden.filter(
    (row) => row.ofBenefit && row.careSetting === "OTHER"
  );
  const otherKinds = hidden.filter((row) => !row.ofBenefit);

  return (
    <div className={className} data-testid="lineAnalysis">
      <span className="block text-xs font-bold uppercase text-color-secondary mb-2">
        Line analysis
      </span>
      {isError && (
        <Message
          severity="error"
          text="The line items could not be analysed."
        />
      )}
      {analysis && (
        <div className="flex flex-column gap-2">
          <p className="m-0" data-testid="lineAnalysis.summary">
            {analysis.summary}
          </p>
          {analysis.note && (
            <Message
              severity="warn"
              text={analysis.note}
              data-testid="lineAnalysis.settingsChanged"
            />
          )}
          <Group
            label={`${readHere.length} more ${
              readHere.length === 1 ? "line" : "lines"
            } in the care ${
              analysis.readSources.length > 1 ? "settings" : "setting"
            } it read, not shown below`}
            count={readHere.length}
            testId="lineAnalysis.readHere"
            defaultOpen
          >
            <RowList rows={readHere} />
          </Group>
          <Group
            label={`${otherSettings.length} ${
              otherSettings.length === 1 ? "line" : "lines"
            } in other care settings — not read`}
            count={otherSettings.length}
            testId="lineAnalysis.otherSettings"
          >
            <OtherSettings
              rows={otherSettings}
              reason={analysis.groupReasons.otherCareSettings}
            />
          </Group>
          <Group
            label={`${otherKinds.length} ${
              otherKinds.length === 1 ? "line" : "lines"
            } of other kinds — not this benefit`}
            count={otherKinds.length}
            testId="lineAnalysis.otherKinds"
          >
            <OtherKinds
              rows={otherKinds}
              reason={analysis.groupReasons.otherKinds}
            />
          </Group>
        </div>
      )}
    </div>
  );
}
